import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Schools } from '../../core/models/schools';
import { ErrorStateMatcher } from '@angular/material/core';
import { UntypedFormControl, FormGroupDirective, NgForm, UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { AuthenticationService } from '../../core/services/authentication.service';
import { SnackBarAlertsService } from '../../core/services/snackbar-alerts.service';
import { SchoolsService } from '../../core/services/schools.service';
import { toYearMonthDay } from '../../core/utilities/index';
import { Subscriptions } from '../../shared/subscriptions';
import { Subscription, Observable } from 'rxjs';
import { SelectOptions } from '../../core/utilities/select-options';

/** Error when invalid control is dirty, touched, or submitted. This allows us to validate the form as the user is typing. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-school-general-information',
  templateUrl: './school-general-information.component.html',
  styleUrls: ['./school-general-information.component.scss']
})
export class SchoolGeneralInformationComponent implements OnInit, OnDestroy {
  @Input() selectedSchoolId: string;

  schoolData: Schools;
  responseData: any;
  editModeOff: boolean;
  isSchoolUser: boolean;
  wrongUser: boolean;
  form: UntypedFormGroup;
  isServicerConfiguration = false;
  subscriptions = new Subscriptions();
  eligible = SelectOptions.YesNo;

  // being used for showing error while typing
  matcher = new MyErrorStateMatcher();

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthenticationService,
    private alerts: SnackBarAlertsService,
    private schoolsService: SchoolsService
  ) {
    this.isServicerConfiguration = this.authService.isServicerConfigUser();
    this.isSchoolUser = this.authService.isSchoolUser();
    if ( this.isSchoolUser === true || this.isServicerConfiguration === true) {
      this.wrongUser === false;
    }
  }

  ngOnInit(): void {
    this.schoolsService.getSchoolsById(this.selectedSchoolId).subscribe(
      data => {
        this.schoolData = data;
        this.createForm();
      },
      error => {
        console.log(error);
      }
    );
  }

  createForm() {
    if (this.schoolData.titleivEligibility.eligible === null) {
      this.form = this.fb.group({
        eligible: [{ value: this.eligible[1], disabled: false }]
      });
    } else {
      this.editModeOff = false;
      this.eligible = SelectOptions.booleanOptionsFor(this.schoolData.titleivEligibility.eligible);
      this.form = this.fb.group({
        eligible: [{ value: this.eligible[0], disabled: false }],
        schoolName: [{ value: this.schoolData.schoolName, disabled: false }],
        schoolCode: [{ value: this.schoolData.schoolCode, disabled: false }],
        schoolType: [{ value: this.schoolData.schoolType, disabled: false }],
        opeid: [{ value: this.schoolData.opeId, disabled: false }],
        effectiveDate: [{ value: this.schoolData.titleivEligibility.effectiveDate, disabled: false }]
      });
    }
  }

  // reverts form to previous values
  revertForm() {
    this.createForm();
    this.form.markAsPristine();
  }

  // edit or cancel form
  editForm() {
    this.editModeOff = true;
  }
  cancelEdit() {
    this.form.disable();
    this.editModeOff = false;
  }

  submit() {
    const effectiveDate = toYearMonthDay(this.form.get('effectiveDate').value);
    const originalSchoolObj = this.schoolData;
    const formObj = {
      schoolName: this.form.value.schoolName,
      titleivEligibility: { eligible: this.form.value.eligible.value, effectiveDate },
      opeId: this.form.value.opeid,
      schoolType: this.form.value.schoolType,
      schoolCode: this.form.value.schoolCode
    };
    /* merge this into the form object, allowing form value to overwrite original values */
    const mergedObj = { ...originalSchoolObj, ...formObj };
    this.subscriptions.add = this.schoolsService.updateSchool(this.selectedSchoolId, mergedObj).subscribe(
      data => {
        this.responseData = data;
        this.form.patchValue(this.responseData);
        this.schoolData = this.responseData;
        this.editModeOff = false;
        this.alerts.show('Update was successful!');
      },
      () => {
        this.alerts.show('Update has failed!');
      }
    );
  }

  // destroying our subscriptions
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
